import { z } from "zod";

/**
 * Event costs + the $750-target contribution scoreboard (Phase 5). Costs are
 * entered as dollar inputs in the UI and converted to cents at submit (see
 * lib/money.ts's dollarsToCents) — never computed client-side beyond that
 * parse. `revenue_cents` / `contribution_cents` / `delta_cents` always come
 * straight from the server.
 */

export const EventCostsSchema = z.object({
  second_bartender_cents: z.number(),
  consumables_cents: z.number(),
  shooter_cents: z.number(),
  fuel_cents: z.number(),
  other_cents: z.number(),
  notes: z.string().nullable().optional(),
});
export type EventCosts = z.infer<typeof EventCostsSchema>;

// Playbook defaults (per Phase 5 build note), shown as the form's starting
// point whenever the event has no costs saved yet. "None saved" is detected
// as "every cost field is exactly zero" — see EventCostsCard — since the
// contribution endpoint has no separate has-been-saved flag.
export const DEFAULT_EVENT_COSTS_CENTS = {
  second_bartender_cents: 17500,
  consumables_cents: 25000,
  shooter_cents: 10000,
  fuel_cents: 7500,
  other_cents: 0,
} as const;

export function costsAreUnset(costs: EventCosts): boolean {
  return (
    costs.second_bartender_cents === 0 &&
    costs.consumables_cents === 0 &&
    costs.shooter_cents === 0 &&
    costs.fuel_cents === 0 &&
    costs.other_cents === 0
  );
}

export const ContributionSchema = z.object({
  revenue_cents: z.number(),
  costs: EventCostsSchema,
  total_costs_cents: z.number(),
  contribution_cents: z.number(),
  target_cents: z.number(),
  delta_cents: z.number(),
});
export type Contribution = z.infer<typeof ContributionSchema>;

// ---- Mutation inputs -----------------------------------------------------

export interface SetEventCostsInput {
  eventId: string;
  second_bartender_cents: number;
  consumables_cents: number;
  shooter_cents: number;
  fuel_cents: number;
  other_cents: number;
  notes?: string | null;
}
